import { z } from 'zod';

/**
 * War-plan singleton — a small set of strategic settings shown in a side
 * panel on the map. Edited by admins; visible to every authenticated user.
 *
 * Labels are bilingual FR/EN; only the enum value is persisted server-side,
 * the human label mapping lives in the web app.
 */

export const warStyleSchema = z.enum(['offensive', 'balanced', 'defensive']);
export type WarStyle = z.infer<typeof warStyleSchema>;

export const opFrequencySchema = z.enum(['high', 'medium', 'low']);
export type OpFrequency = z.infer<typeof opFrequencySchema>;

export const stockManagementSchema = z.enum(['frontline_supply', 'balanced_use']);
export type StockManagement = z.infer<typeof stockManagementSchema>;

export const griefingRiskSchema = z.enum(['high', 'medium', 'low']);
export type GriefingRisk = z.infer<typeof griefingRiskSchema>;

export const warPlanTitleSchema = z.string().trim().min(1).max(120);

export const warPlanSchema = z.object({
  title: warPlanTitleSchema,
  warStyle: warStyleSchema,
  opFrequency: opFrequencySchema,
  stockManagement: stockManagementSchema,
  griefingRisk: griefingRiskSchema,
  updatedAt: z.string().datetime(),
  updatedBy: z.string().nullable(),
});
export type WarPlanDTO = z.infer<typeof warPlanSchema>;

export const patchWarPlanSchema = z
  .object({
    title: warPlanTitleSchema.optional(),
    warStyle: warStyleSchema.optional(),
    opFrequency: opFrequencySchema.optional(),
    stockManagement: stockManagementSchema.optional(),
    griefingRisk: griefingRiskSchema.optional(),
  })
  .refine((p) => Object.keys(p).length > 0, {
    message: 'Patch must include at least one field',
  });
export type PatchWarPlanInput = z.infer<typeof patchWarPlanSchema>;

export const DEFAULT_WAR_PLAN: Omit<WarPlanDTO, 'updatedAt' | 'updatedBy'> = {
  title: 'Foxhole War-Plan',
  warStyle: 'balanced',
  opFrequency: 'medium',
  stockManagement: 'balanced_use',
  griefingRisk: 'medium',
};
